import type { SeedTables } from '../types/localDb.types'
import { buildPlatformSeed } from './platform.seed'
import { applyClaimData } from './wms-claim.seed'
import { applyDestructionData } from './wms-destruction.seed'
import { buildWmsFgSeed } from './wms-fg.seed'
import { buildWmsInboundSeed, OPENING_STOCK_RACK } from './wms-inbound.seed'
import { applyInventoryData } from './wms-inventory.seed'
import { buildWmsMasterSeed } from './wms-master.seed'
import { applyMasterData } from './wms-master-data.seed'
import { applyMergeData } from './wms-merge.seed'
import { buildWmsNotificationSeed } from './wms-notification.seed'
import { applyOpnameData } from './wms-opname.seed'
import { applyOutboundStock, buildWmsOutboundSeed } from './wms-outbound.seed'
import { buildWmsProcurementSeed } from './wms-procurement.seed'
import { buildWmsRequestSeed } from './wms-request.seed'
import { buildWmsReturnSeed } from './wms-return.seed'
import { applySetupData } from './wms-setup.seed'
import { buildWmsTransferSeed } from './wms-transfer.seed'
import { buildWorkflowBapSeed } from './workflow-bap.seed'
import { buildWorkflowClmSeed } from './workflow-clm.seed'
import { buildWorkflowDspSeed } from './workflow-dsp.seed'
import { buildWorkflowFgSeed } from './workflow-fg.seed'
import { buildWorkflowGabSeed } from './workflow-gab.seed'
import { buildWorkflowKslSeed } from './workflow-ksl.seed'
import { buildWorkflowOfgSeed } from './workflow-ofg.seed'
import { buildWorkflowOpnSeed } from './workflow-opn.seed'
import { buildWorkflowPrcSeed } from './workflow-prc.seed'
import { buildWorkflowQcSeed } from './workflow-qc.seed'
import { buildWorkflowReqSeed } from './workflow-req.seed'
import { buildWorkflowRtnSeed } from './workflow-rtn.seed'
import { buildWorkflowTobSeed } from './workflow-tob.seed'
import { buildWorkflowTobReturnSeed } from './workflow-tobreturn.seed'
import { buildWorkflowTrfSeed } from './workflow-trf.seed'
import { buildWorkflowWipSeed } from './workflow-wip.seed'

export { LOCAL_DB_PERSONAS } from './platform.seed'

/** Naikkan setiap kali bentuk seed berubah — browser yang versinya beda akan di-reset otomatis. */
export const LOCAL_DB_SEED_VERSION = 'jal-wms-2026.10.28-v750-t18-prc-spv-self-approve'

/** Gabungkan seed per collection; tabel yang sama dari beberapa domain (mis. `wml_*`, `rm_unit`) disambung. */
export function mergeSeeds(...parts: SeedTables[]): SeedTables {
  return parts.reduce<SeedTables>((merged, part) => {
    for (const [collection, rows] of Object.entries(part)) {
      merged[collection] = [...(merged[collection] ?? []), ...rows]
    }
    return merged
  }, {})
}

/** Stok awal: lokasi rak + satuan besar (prototype `rm.units` In Stock). */
function placeOpeningStock(seed: SeedTables): SeedTables {
  const bins = new Map((seed.mt_rack_bin ?? []).map(bin => [String(bin.code), bin]))
  const unitNames = new Map((seed.mt_unit ?? []).map(unit => [String(unit.id), String(unit.name)]))
  const materials = new Map((seed.mt_material ?? []).map(material => [String(material.id), material]))
  const units = (seed.rm_unit ?? []).map(unit => {
    const rack = OPENING_STOCK_RACK[String(unit.qr_code)]
    if (!rack) return unit
    const material = materials.get(String(unit.material_id))
    const perBig = Number(material?.default_purchase_unit_qty ?? 1)
    return {
      ...unit,
      rack_bin_id: bins.get(rack)?.id ?? null,
      warehouse_zone_id: seed.mt_rack?.find(r => r.id === bins.get(rack)?.rack_id)?.zone_id ?? null,
      small_per_big_snapshot: perBig,
      unit_big_snapshot: unitNames.get(String(material?.default_purchase_unit_id)) ?? null,
      stored_at: unit.stored_at ?? unit.date_created ?? null
    }
  })
  return { ...seed, rm_unit: units }
}

function buildBaseSeed(): SeedTables {
  return applySetupData(
    applyDestructionData(
      applyClaimData(
        applyMergeData(
          applyOutboundStock(
            placeOpeningStock(
              mergeSeeds(
                buildPlatformSeed(),
                buildWorkflowPrcSeed(),
                buildWorkflowTobSeed(),
                buildWorkflowQcSeed(),
                buildWorkflowRtnSeed(),
                buildWorkflowReqSeed(),
                buildWorkflowTrfSeed(),
                buildWorkflowWipSeed(),
                buildWorkflowTobReturnSeed(),
                buildWorkflowFgSeed(),
                buildWorkflowOfgSeed(),
                buildWorkflowGabSeed(),
                buildWorkflowKslSeed(),
                buildWorkflowClmSeed(),
                buildWorkflowDspSeed(),
                buildWorkflowBapSeed(),
                buildWorkflowOpnSeed(),
                buildWmsMasterSeed(),
                buildWmsProcurementSeed(),
                buildWmsInboundSeed(),
                buildWmsReturnSeed(),
                buildWmsRequestSeed(),
                buildWmsTransferSeed(),
                buildWmsFgSeed(),
                buildWmsOutboundSeed(),
                buildWmsNotificationSeed()
              )
            )
          )
        )
      )
    )
  )
}

export function buildLocalDbSeed(): SeedTables {
  return applyMasterData(applyOpnameData(applyInventoryData(buildBaseSeed())))
}
